import { Button } from "@/components/ui/button";
import { Music, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { DASHBOARD_SIGNUP_URL } from "@/config/urls";
import { useRef, useState, useEffect } from "react";

const CtaSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-20 relative overflow-hidden bg-cover bg-center bg-ophira-dark" style={{
      backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.8)), url('https://images.unsplash.com/photo-1470225620780-dba8ba36b745?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')`
    }}>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-olive-gradient opacity-5"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-noise-pattern bg-[length:300px_300px] opacity-20"></div>
        
        {/* Animated blobs */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-20">
          <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-olive-600/20 blob-animation" style={{animationDelay: '0s'}}></div>
          <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-cream-300/20 blob-animation" style={{animationDelay: '2s'}}></div>
        </div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className={`max-w-5xl mx-auto olive-card p-12 md:p-16 text-center relative overflow-hidden transition-all duration-1000 ${
          isInView ? 'animate-scale-up' : 'opacity-0 scale-90'
        }`}>
          {/* Glow effects */}
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-olive-500/10 blur-[100px]"></div>
          <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-earthbrown-400/10 blur-[100px]"></div>
          
          {/* Diagonal lines */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full border-b border-olive-500/5 -skew-y-6"></div>
            <div className="absolute top-10 left-0 w-full h-full border-b border-cream-300/5 -skew-y-12"></div>
          </div>
          
          <h2 className={`text-3xl md:text-4xl lg:text-5xl font-bold mb-6 transition-all duration-700 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            Ready to <span className="olive-gradient-text relative">Amplify</span> Your Music Career?
          </h2>
          <p className={`text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto mb-10 transition-all duration-700 delay-100 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            Join thousands of independent artists who are growing their audience and earning 100% ownership of their music while keeping full ownership of their music.
          </p>
          <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-200 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <a href={DASHBOARD_SIGNUP_URL}>
              <Button className="olive-button text-base px-8 py-6 w-full sm:w-auto group">
                <Music className="mr-2" size={18} /> Get Started Today
                <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
              </Button>
            </a>
            <Link to="/contact">
              <Button variant="outline" className="text-base px-8 py-6 border-ophira-yellow/30 bg-background/60 hover:bg-black/40 hover:text-white w-full sm:w-auto group">
                Contact Us
                <ArrowRight size={16} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CtaSection;
